import { useState, useEffect, useCallback } from "react";
import apiClient from "../services/axios";
import { AxiosError } from "axios";

interface UseSearchResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  search: (term: string) => void;
  loadMore: () => Promise<void>;
  allLoaded: boolean;
  updateUrl: (newUrl: string) => void;
}

interface PaginatedResponse<T> {
  results: T[];
}

/**
 * Custom hook for paginated and searchable GET requests using the application's axios instance
 * @param baseUrl The endpoint URL to fetch data from (without base URL)
 * @param initialSearchTerm Optional initial search term
 * @returns Object containing { data, loading, error, refetch, search, loadMore, allLoaded }
 */
function useSearch<T = any>(
  baseUrl: string,
  initialSearchTerm: string = ""
): UseSearchResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
  const [allLoaded, setAllLoaded] = useState<boolean>(false);
  const [url, setUrl] = useState<string>(baseUrl);
  const [lastQuery, setLastQuery] = useState<{
    page: number;
    searchTerm: string;
  }>({ page: 1, searchTerm: initialSearchTerm });

  const fetchData = useCallback(
    async (opts?: { reset?: boolean }) => {
      setLoading(true);
      setError(null);
      try {
        let response;

        // Check if this is a model search URL that needs to be converted to unified API
        const modelSearchMatch = url.match(/^\/get\/model_search\/(.+)$/);

        if (modelSearchMatch) {
          // Use unified API for model search
          const modelName = modelSearchMatch[1];
          response = await apiClient.post<any[]>("/odash/execute", {
            action: "get_model_search",
            parameters: {
              model_name: modelName,
              page,
              ...(searchTerm ? { search: searchTerm } : {}),
            },
          });
          // Unified API returns data in response.data
          const results = response.data || [];
          setAllLoaded(results.length === 0);
          if (opts?.reset) {
            setData(results);
          } else {
            setData((prev) => [...prev, ...results]);
          }
        } else {
          // Use original GET request for other URLs
          response = await apiClient.get<any[]>(url, {
            params: {
              page,
              ...(searchTerm ? { search: searchTerm } : {}),
            },
          });

          const results = response.data || [];
          setAllLoaded(results.length === 0);
          if (opts?.reset) {
            setData(results);
          } else {
            setData((prev) => [...prev, ...results]);
          }
        }
      } catch (err) {
        const axiosError = err as AxiosError;
        setError(
          new Error(
            (axiosError.response?.data as any)?.message ||
              axiosError.message ||
              "An error occurred while fetching data"
          )
        );
      } finally {
        setLoading(false);
      }
      setLastQuery({ page, searchTerm });
    },
    [url, page, searchTerm]
  );

  // Fetch on mount and when page/searchTerm changes
  useEffect(() => {
    fetchData({ reset: page === 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, url]);

  // Refetch current page/search
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response;

      // Check if this is a model search URL that needs to be converted to unified API
      const modelSearchMatch = url.match(/^\/get\/model_search\/(.+)$/);

      if (modelSearchMatch) {
        // Use unified API for model search
        const modelName = modelSearchMatch[1];
        response = await apiClient.post<PaginatedResponse<T>>(
          "/odash/execute",
          {
            action: "get_model_search",
            parameters: {
              model_name: modelName,
              page: lastQuery.page,
              ...(lastQuery.searchTerm ? { search: lastQuery.searchTerm } : {}),
            },
          }
        );
        // Unified API returns data in response.data
        const results = response.data.results || [];
        setAllLoaded(results.length === 0);
        setData(results);
      } else {
        // Use original GET request for other URLs
        response = await apiClient.get<PaginatedResponse<T>>(url, {
          params: {
            page: lastQuery.page,
            ...(lastQuery.searchTerm ? { search: lastQuery.searchTerm } : {}),
          },
        });
        const results = response.data.results || [];
        setAllLoaded(results.length === 0);
        setData(results);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      setError(
        new Error(
          (axiosError.response?.data as any)?.message ||
            axiosError.message ||
            "An error occurred while fetching data"
        )
      );
    } finally {
      setLoading(false);
    }
  }, [url, lastQuery]);

  // Set a new search term, reset page and data
  const search = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(1);
    setAllLoaded(false);
  }, []);

  const updateUrl = (newUrl: string) => {
    setUrl(newUrl);
    setPage(1);
    setAllLoaded(false);
  };

  // Load next page
  const loadMore = useCallback(async () => {
    if (loading || allLoaded) return;
    setPage((prev) => prev + 1);
  }, [loading, allLoaded]);

  return {
    data,
    loading,
    error,
    refetch,
    search,
    loadMore,
    allLoaded,
    updateUrl,
  };
}

export default useSearch;
